import { dedupeFrequencyRowsByHour } from "./methodTripStats.js";

const METHOD_KEYS = [
  "method1",
  "method2",
  "method3",
  "method4_0",
  "method4_10",
  "method4_20",
  "method4_30",
  "method5",
  "method6",
];

const METHOD4_BETAS = [0, 0.1, 0.2, 0.3];

function hasNonEmptyArray(value) {
  return Array.isArray(value) && value.length > 0;
}

function firstNonEmptyArray(...candidates) {
  for (const c of candidates) {
    if (hasNonEmptyArray(c)) return c;
  }
  return [];
}

/** Beta (0|0.1|0.2|0.3) → 0|10|20|30 */
export function resolveMethod4Percent(beta) {
  const n = Number(beta);
  if (!Number.isFinite(n)) {
    console.error("Tanımsız Ceder aşım payı", beta);
    return 0;
  }
  const pct = Math.round(n * 100);
  if ([0, 10, 20, 30].includes(pct)) return pct;
  console.error("Tanımsız Ceder aşım payı, aşım yok kullanılıyor", beta);
  return 0;
}

/** Beta → method4_10 | method4_20 | method4_30 */
export function resolveMethod4Key(beta) {
  return `method4_${resolveMethod4Percent(beta)}`;
}

/**
 * Frekans grid satırları — önce optimizasyon yanıtı, sonra tam analiz yedekleri.
 * @param {'gidis'|'donus'} direction
 */
export function getMethodFrequencySlice(
  optimizationData,
  analysisData,
  direction,
  methodKey
) {
  return firstNonEmptyArray(
    optimizationData?.[direction]?.[methodKey],
    analysisData?.optimizasyon_sonuclari?.[direction]?.[methodKey],
    analysisData?.[direction]?.[methodKey]
  );
}

/** Backend sefer listesi — aynı öncelik sırası. */
export function getMethodSeferlerSlice(
  optimizationData,
  analysisData,
  methodKey
) {
  return firstNonEmptyArray(
    optimizationData?.seferler?.[methodKey],
    analysisData?.optimizasyon_sonuclari?.seferler?.[methodKey]
  );
}

function remapLegacyMethod4Keys(container) {
  if (!container || typeof container !== "object") return;

  const maybeCopy = (fromKey, toKey) => {
    if (container[fromKey] == null) return;
    if (!hasNonEmptyArray(container[toKey]) && hasNonEmptyArray(container[fromKey])) {
      container[toKey] = container[fromKey];
    }
  };

  for (const k of Object.keys(container)) {
    const m = String(k).match(/^method4[_-]?beta(\d+)$/i);
    if (!m) continue;
    const pct = Number(m[1]);
    if ([0, 10, 20, 30].includes(pct)) maybeCopy(k, `method4_${pct}`);
  }

  maybeCopy("method4_beta0", "method4_0");
  maybeCopy("method4_beta10", "method4_10");
  maybeCopy("method4_beta20", "method4_20");
  maybeCopy("method4_beta30", "method4_30");
}

function preprocessFrequencyPayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  const copy = {
    ...payload,
    gidis: { ...(payload.gidis || {}) },
    donus: { ...(payload.donus || {}) },
    seferler: { ...(payload.seferler || {}) },
  };
  remapLegacyMethod4Keys(copy.gidis);
  remapLegacyMethod4Keys(copy.donus);
  remapLegacyMethod4Keys(copy.seferler);
  return copy;
}

function hasMethodFrequencyData(obj) {
  if (!obj?.gidis || !obj?.donus) return false;
  return METHOD_KEYS.some(
    (k) => hasNonEmptyArray(obj.gidis[k]) || hasNonEmptyArray(obj.donus[k])
  );
}

function pickFrequencySource(primary, tamAnalizSonuclar) {
  if (hasMethodFrequencyData(primary)) return primary;

  const fromTamOpt = tamAnalizSonuclar?.optimizasyon_sonuclari;
  if (hasMethodFrequencyData(fromTamOpt)) {
    console.warn(
      "Frekans optimizasyon yanıtı boş; tam analiz optimizasyon_sonuclari kullanılıyor."
    );
    return fromTamOpt;
  }

  if (!tamAnalizSonuclar?.gidis || !tamAnalizSonuclar?.donus) {
    return primary?.error ? primary : null;
  }

  const built = {
    gidis: {},
    donus: {},
    seferler:
      primary?.seferler ||
      fromTamOpt?.seferler ||
      tamAnalizSonuclar?.optimizasyon_sonuclari?.seferler ||
      {},
  };

  let filled = false;
  for (const k of METHOD_KEYS) {
    if (Array.isArray(tamAnalizSonuclar.gidis[k])) {
      built.gidis[k] = tamAnalizSonuclar.gidis[k];
      if (tamAnalizSonuclar.gidis[k].length) filled = true;
    }
    if (Array.isArray(tamAnalizSonuclar.donus[k])) {
      built.donus[k] = tamAnalizSonuclar.donus[k];
      if (tamAnalizSonuclar.donus[k].length) filled = true;
    }
  }

  if (filled) {
    console.warn(
      "Frekans optimizasyon yanıtı boş; tam analiz frekans gridleri kullanılıyor."
    );
    return built;
  }

  return primary?.error ? primary : null;
}

/** Boş method anahtarlarını tam analiz yedeklerinden doldurur. */
function mergeMissingMethodGrids(result, tamAnalizSonuclar) {
  if (!tamAnalizSonuclar || !result) return;

  const sources = [
    tamAnalizSonuclar.optimizasyon_sonuclari,
    tamAnalizSonuclar,
  ].filter((s) => s && typeof s === "object");

  for (const src of sources) {
    if (!src.gidis || !src.donus) continue;
    for (const k of METHOD_KEYS) {
      if (!hasNonEmptyArray(result.gidis[k]) && hasNonEmptyArray(src.gidis[k])) {
        result.gidis[k] = JSON.parse(JSON.stringify(src.gidis[k]));
      }
      if (!hasNonEmptyArray(result.donus[k]) && hasNonEmptyArray(src.donus[k])) {
        result.donus[k] = JSON.parse(JSON.stringify(src.donus[k]));
      }
      if (result.seferler && src.seferler) {
        if (
          !hasNonEmptyArray(result.seferler[k]) &&
          hasNonEmptyArray(src.seferler[k])
        ) {
          result.seferler[k] = JSON.parse(JSON.stringify(src.seferler[k]));
        }
      }
    }
  }
}

/**
 * Store'a yazılmadan önce optimizasyon verisini normalize eder.
 * @param {object|null} raw - /frekans-optimizasyon sonuclar
 * @param {object|null} tamAnalizSonuclar - /tam-analiz sonuclar
 */
export function normalizeOptimizationData(raw, tamAnalizSonuclar = null) {
  const preppedRaw = preprocessFrequencyPayload(raw);
  const preppedTam =
    tamAnalizSonuclar && typeof tamAnalizSonuclar === "object"
      ? {
          ...tamAnalizSonuclar,
          optimizasyon_sonuclari: preprocessFrequencyPayload(
            tamAnalizSonuclar.optimizasyon_sonuclari
          ),
          gidis: tamAnalizSonuclar.gidis
            ? { ...tamAnalizSonuclar.gidis }
            : tamAnalizSonuclar.gidis,
          donus: tamAnalizSonuclar.donus
            ? { ...tamAnalizSonuclar.donus }
            : tamAnalizSonuclar.donus,
        }
      : null;

  const picked = pickFrequencySource(preppedRaw, preppedTam);
  if (!picked) return null;

  if (picked.error && !hasMethodFrequencyData(picked)) {
    console.error("Optimizasyon hatası:", picked.error);
    return null;
  }

  const result = {
    gidis: { ...(picked.gidis || {}) },
    donus: { ...(picked.donus || {}) },
    seferler: { ...(picked.seferler || {}) },
  };

  remapLegacyMethod4Keys(result.gidis);
  remapLegacyMethod4Keys(result.donus);
  remapLegacyMethod4Keys(result.seferler);

  mergeMissingMethodGrids(result, tamAnalizSonuclar);

  for (const k of METHOD_KEYS) {
    if (!result.gidis[k]) result.gidis[k] = [];
    if (!result.donus[k]) result.donus[k] = [];
    if (!result.seferler[k]) result.seferler[k] = [];
  }

  for (const side of ["gidis", "donus", "seferler"]) {
    for (const k of Object.keys(result[side])) {
      if (hasNonEmptyArray(result[side][k])) {
        let rows = JSON.parse(JSON.stringify(result[side][k]));
        if (side === "gidis" || side === "donus") {
          rows = dedupeFrequencyRowsByHour(rows);
        }
        result[side][k] = rows;
      }
    }
  }

  for (const k of METHOD_KEYS) {
    const meta = picked[k];
    if (meta && typeof meta === "object" && !Array.isArray(meta)) {
      result[k] = { ...meta };
    }
  }

  // Katalog A1→A2 özeti — yalnız typeOutcome; grid alanlarıyla karıştırma
  if (raw && typeof raw === "object" && raw.typeOutcome) {
    result.typeOutcome = raw.typeOutcome;
  }

  return result;
}

export { METHOD_KEYS, hasMethodFrequencyData, dedupeFrequencyRowsByHour };
